import React from 'react';
import { Box } from '@mui/material';
import type { Project } from '../data/projects';
import ResponsiveImage from './ResponsiveImage';

interface ProjectCardProps {
  project: Project;
  /**
   * Accessible description of the photo. The gallery is caption-free, so this
   * is derived from the category rather than shown on screen.
   */
  alt: string;
}

// Tiles sit in a 1/2/3-column masonry, so each spans roughly the viewport, half,
// then a third of the ~1200px container.
const CARD_SIZES = '(max-width: 600px) 100vw, (max-width: 900px) 50vw, 400px';

/**
 * A single gallery tile: the photo alone, in its natural aspect ratio — no
 * title, description or year. Hovering lifts the tile and eases the image in a
 * touch, which keeps the grid feeling alive without adding text back.
 */
const ProjectCard: React.FC<ProjectCardProps> = ({ project, alt }) => (
  <Box
    sx={{
      overflow: 'hidden',
      borderRadius: 2,
      boxShadow: 3,
      // Placeholder tint so a tile has presence before its photo decodes.
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      transition: 'transform .35s ease, box-shadow .35s ease',
      '& img': { transition: 'transform .5s ease' },
      // Pointer devices only — on touch, :hover sticks after a tap.
      '@media (hover: hover)': {
        '&:hover': { transform: 'translateY(-4px)', boxShadow: 8 },
        '&:hover img': { transform: 'scale(1.04)' },
      },
    }}
  >
    <ResponsiveImage src={project.thumbnail} alt={alt} sizes={CARD_SIZES} />
  </Box>
);

export default ProjectCard;
